import dayjs from 'dayjs';

import { AppointmentItem } from 'store/appointments/appointments.types';

import { MINUTES_TO_MEETING } from 'constants/defaults';

import {
  AppointmentTypeItem,
  BaseAppointmentProps,
  CancellationInfoProps
} from 'models/appointment.types';

import { CANCEL_REASON_LABELS, getReasonCode } from './cancelation';
import { DateFormat } from './date';
import { APPOINTMENT_TYPE_ID_REGEX } from './regExp';

/**
 * True once a scheduled appointment's join window has expired (LMD-329).
 *
 * Mirrors the backend's own upcoming-list cutoff (`getAndIfUpcomingAppointment`
 * in `packages/appointment`, `lifemd` repo): a video/scheduled appointment
 * stops being joinable once `appointmentTime.endTime` elapses, with no grace
 * period — the grace-period concept is reserved for the separate "missed
 * appointment" determination, not for join-prompt visibility.
 *
 * Queue (ASAP) and message-based appointments have no fixed schedule to
 * expire against, so they're never considered expired here — matches the
 * backend's own bypass for those two cases.
 */
export const isJoinWindowExpired = (
  item?: Pick<BaseAppointmentProps, 'appointmentMethod' | 'appointmentTime' | 'category'> | null
): boolean => {
  if (!item || item.category === 'queue' || item.appointmentMethod === 'message') return false;
  const endTime = item.appointmentTime?.endTime;
  return !!endTime && dayjs().isAfter(dayjs(endTime));
};

export const parseUpcomingAppointment = (item: AppointmentItem) => {
  const startTime = item.appointmentTime?.startTime;
  const diff = dayjs().diff(startTime, 'm');

  if (
    startTime &&
    diff >= MINUTES_TO_MEETING &&
    item.appointmentStatus === 'pending' &&
    item.appointmentMethod !== 'message' &&
    !isJoinWindowExpired(item)
  ) {
    return { ...item, isStartsSoon: true };
  }

  if (!startTime && (item.hasStaffJoined || item.patientCanJoin)) {
    return { ...item, isStartsSoon: true };
  }

  return item;
};

export const getDataForAppointmentTimer = (appointmentsList: AppointmentItem[]) => {
  const firstAppointment = appointmentsList?.find(
    (val) => val.isStartsSoon || val.category === 'queue'
  );

  if (!firstAppointment) return;
  return {
    appointmentID: firstAppointment._id,
    category: firstAppointment.category,
    displayWaitTime: firstAppointment.displayWaitTime,
    hasStaffJoined: firstAppointment.hasStaffJoined,
    startTime: firstAppointment.appointmentTime?.startTime
  };
};

export const findAppointmentType = (
  typeToFind: string,
  appointments: AppointmentTypeItem[] = []
): AppointmentTypeItem | undefined => appointments.find((item) => item.code === typeToFind);

const checkIFGivenStringIsAppointmentTypeID = (id: string | null) => {
  if (!id) {
    return false;
  }
  const regex = new RegExp(APPOINTMENT_TYPE_ID_REGEX);
  return regex.test(id);
};

export const findAppointmentTypeByData = (
  types: AppointmentTypeItem[] | undefined,
  query: string | null | undefined
): AppointmentTypeItem | null => {
  if (!query || !types?.length) {
    return null;
  }
  return (
    types.find((type) => {
      if (checkIFGivenStringIsAppointmentTypeID(query)) {
        return type._id === query;
      }
      return (
        type.code?.trim()?.toLowerCase() === query.toLowerCase() ||
        type.displayName.trim().toLowerCase() === query.toLowerCase()
      );
    }) ?? null
  );
};

export const getDataForAppointmentComponent = (
  appointment?: AppointmentItem
): {
  description: string;
  startTime?: string;
  title: string;
  type: 'message' | 'start' | 'default' | 'missed' | 'cancelled';
} => {
  const startTime = appointment?.appointmentTime?.startTime || undefined;
  const title = appointment?.appointmentType.displayName || '';

  const getType = () => {
    if (appointment?.missedAppointment) return 'missed';
    if (appointment?.appointmentStatus === 'cancelled') return 'cancelled';
    if (appointment?.appointmentMethod === 'message') return 'message';
    if (appointment?.isStartsSoon) return 'start';
    return 'default';
  };

  const getCancelledDescription = (cancelationInfo?: CancellationInfoProps) => {
    const reasonCode = getReasonCode(cancelationInfo?.reason);
    const label = reasonCode ? CANCEL_REASON_LABELS[reasonCode] : undefined;
    if (label) return `Cancelled — ${label}`;
    if (cancelationInfo?.isAutomated) return 'Cancelled automatically';
    return 'Cancelled';
  };

  const getDescription = () => {
    if (appointment?.missedAppointment) return 'Missed';
    if (appointment?.appointmentStatus === 'cancelled')
      return getCancelledDescription(appointment.cancelationInfo);
    if (startTime) return dayjs(startTime).format(DateFormat.MMM_D_h_mma_z);
    if (appointment?.category === 'queue') return 'ASAP appointment';
    return 'Message-based consult';
  };

  return {
    description: getDescription(),
    startTime,
    title,
    type: getType()
  };
};

/** Scheduled upcoming visits only (excludes queue / ASAP rows without a fixed slot). */
export const canAddToCalendarOnAppointmentCard = (item: AppointmentItem) =>
  item.category !== 'queue' && !!item.appointmentTime?.startTime && !!item.appointmentTime?.endTime;
